import { LockClosedIcon } from '@heroicons/react/24/outline'

import { anim, prog, stagger, travel } from '../../../motion'
import { Fit, Line } from '../../../v2/kit'
import { useCopy } from '../../i18n'

// Bühne 1200 × 520: Pipeline oben, ATS und Datenfluss unten
const STAGE_X = [110, 350, 590, 830, 1070]
const PIPE_Y = 150

// Bewerbungen als Punkte, `fit` entscheidet, wer nach der Vorauswahl weiterzieht
const APPLICANTS = Array.from({ length: 15 }, (_, i) => ({
  x: STAGE_X[1] - 70 + (i % 5) * 35,
  y: PIPE_Y + 64 + Math.floor(i / 5) * 32,
  fit: [2, 6, 9, 13].includes(i) ? 'high' : i % 3 === 0 ? 'mid' : 'low'
}))
const PASSED = APPLICANTS.filter((a) => a.fit === 'high')

const ATS = { x: 150, y: 400 }
const AIVY = { x: 760, y: 400 }

export default function DemoProcess() {
  const c = useCopy('prozess')

  return (
    <div className='cin-full'>
      <div className='cin-pad cin-pad--top dm-process-head'>
        <Line at={[0, 0.06]} className='cin-eyebrow cin-eyebrow--light'>
          {c.eyebrow}
        </Line>
        <Line at={[0.02, 0.1]} className='cin-title cin-title--sm'>
          {c.title}
        </Line>
      </div>

      <Fit w={1200} h={520} maxW={0.9} maxH={0.56} className='dm-process-fit'>
        <div className='dm-pipe' style={{ top: PIPE_Y }}>
          <i style={{ ...prog([0.04, 0.16]), transform: 'scaleX(var(--p))' }} />
        </div>

        {c.stages.map((s, i) => (
          <div
            key={s}
            className='dm-stage'
            style={{
              left: STAGE_X[i],
              top: PIPE_Y,
              ...anim({ at: stagger(0.04, 0.025, 0.06, i), s: 0.5, y: 0 })
            }}
          >
            <span className='dm-stage__node' />
            <span className='dm-stage__label'>{s}</span>
          </div>
        ))}

        <div
          className='dm-addon-chip'
          style={{
            left: 0,
            top: PIPE_Y - 110,
            ...anim({ at: [0.78, 0.84], y: 20 })
          }}
        >
          {c.addons[0]}
        </div>
        <div
          className='dm-addon-chip'
          style={{
            right: 0,
            top: PIPE_Y - 110,
            ...anim({ at: [0.8, 0.86], y: 20 })
          }}
        >
          {c.addons[1]}
        </div>
        <div
          className='dm-later'
          style={{
            left: STAGE_X[3],
            top: PIPE_Y - 64,
            ...anim({ at: [0.82, 0.88], y: 20 })
          }}
        >
          {c.later}
        </div>

        <div
          className='dm-aivy-pin'
          style={{
            left: STAGE_X[1],
            top: PIPE_Y,
            ...anim({ at: [0.14, 0.2], y: -160, s: 0.6 })
          }}
        >
          <span className='dm-aivy-pin__badge'>{c.badge}</span>
          <span className='dm-aivy-pin__logo'>Aivy</span>
        </div>

        {APPLICANTS.map((a, i) => (
          <div
            key={i}
            className='dm-applicant'
            style={{
              left: a.x,
              top: a.y,
              ...anim({
                at: stagger(0.06, 0.008, 0.06, i),
                // Wer passt, zieht weiter, das Original verschwindet beim Start
                out:
                  a.fit === 'high'
                    ? stagger(0.34, 0.02, 0.01, PASSED.indexOf(a))
                    : undefined,
                x: -420,
                y: 0,
                oy: 0
              })
            }}
          >
            <i
              className={`dm-applicant__fit dm-applicant__fit--${a.fit}`}
              style={{
                ...prog([0.24 + (i % 5) * 0.01, 0.3 + (i % 5) * 0.01]),
                opacity: 'var(--p)'
              }}
            />
          </div>
        ))}
        {PASSED.map((a, i) => (
          <div
            key={`p${i}`}
            className='dm-applicant dm-applicant--moving'
            style={travel({
              at: stagger(0.34, 0.02, 0.1, i),
              from: [a.x, a.y],
              to: [STAGE_X[2] - 40 + i * 26, PIPE_Y + 64],
              keep: true
            })}
          />
        ))}

        <div
          className='dm-store'
          style={{
            left: ATS.x - 130,
            top: ATS.y - 50,
            ...anim({ at: [0.5, 0.56], y: 40 })
          }}
        >
          <div className='dm-store__title'>{c.ats}</div>
          {c.kept.map((k) => (
            <span key={k} className='dm-store__kept'>
              <LockClosedIcon width={14} height={14} />
              {k}
            </span>
          ))}
        </div>
        <div
          className='dm-store dm-store--aivy'
          style={{
            left: AIVY.x,
            top: AIVY.y - 50,
            ...anim({ at: [0.52, 0.58], y: 40 })
          }}
        >
          <div className='dm-store__title'>Aivy</div>
        </div>
        <div
          className='dm-flowline'
          style={{
            left: ATS.x + 140,
            top: ATS.y,
            width: AIVY.x - ATS.x - 150,
            ...prog([0.56, 0.62]),
            transform: 'scaleX(var(--p))'
          }}
        />
        {c.sent.map((s, i) => (
          <div
            key={s}
            className='dm-token'
            style={travel({
              at: stagger(0.6, 0.04, 0.12, i),
              from: [ATS.x + 30, ATS.y - 16 + (i - 1) * 4],
              to: [AIVY.x + 120, AIVY.y - 30 + i * 30],
              keep: true
            })}
          >
            {s}
          </div>
        ))}
      </Fit>

      <div className='dm-process-copy'>
        <Line at={[0.16, 0.22]} out={[0.46, 0.5]} className='dm-process-text'>
          {c.text}
        </Line>
        <Line at={[0.5, 0.56]} out={[0.76, 0.8]} className='dm-process-text'>
          {c.privacy}
        </Line>
        <Line
          at={[0.8, 0.86]}
          className='dm-process-text dm-process-text--proof'
        >
          {c.proof}
        </Line>
      </div>
    </div>
  )
}
